import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card, Table, Tag, Space, Typography, Select, Button, Alert, Input,
  DatePicker, Tooltip, Modal, Row, Col, Statistic, Empty
} from 'antd';
import {
  ReloadOutlined, DeleteOutlined, MonitorOutlined, WarningOutlined,
  ClockCircleOutlined, SearchOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { scanIngestApi } from './scanIngestApi';
import { useAppNotifications } from '../../../../utils/notify';
import PageHeader from '../../../../components/PageHeader';
import useAuth from '../../../../hooks/useAuth';

const { Text } = Typography;
const { RangePicker } = DatePicker;

// Stage SCAN-6 — the Ingest Monitor.
//
// What every scanner has sent, and what the engine decided about each record.
// Design rules this page follows deliberately:
//
//   1. THE VERDICT IS THE HEADLINE. A record's `status` plus the engine's own
//      sentence ("Out of sequence — scan Polishing first.") is what an
//      operator needs; the barcode and timestamps are supporting detail.
//   2. THE SUMMARY ROW ANSWERS "IS ANYTHING STUCK?" without reading the table.
//      A growing RECEIVED count means the apply workers are not running; a
//      growing DEFERRED count means pieces are being scanned out of order.
//   3. APPLIED RECORDS ARE NOT DELETABLE — the checkbox is disabled and the
//      row's delete icon is greyed with a tooltip saying why, rather than
//      offering an action the server will refuse. Deleting one would erase the
//      evidence of a stage advance without undoing the advance itself.
//   4. THE DATE WINDOW IS ALWAYS SET. The table is partitioned on
//      received_at; an unbounded query would read every partition.

const STATUS_META = {
  APPLIED:              { color: 'green',   help: 'The piece advanced.' },
  RECEIVED:             { color: 'blue',    help: 'Stored, not yet applied. A growing count here means the apply workers are not running.' },
  DEFERRED:             { color: 'gold',    help: 'Out of order in a replay — retrying. Becomes EXPIRED after 5 attempts.' },
  REJECTED:             { color: 'red',     help: 'The engine refused it. See the reason column.' },
  EXPIRED:              { color: 'volcano', help: 'Retried 5 times and never succeeded.' },
  IGNORED_REPEAT:       { color: 'default', help: 'The same label re-read inside the repeat window. Not an error.' },
  UNRECOGNIZED_CODE:    { color: 'purple',  help: 'Not a piece barcode and not a control code — a courier label or a bad read.' },
  UNKNOWN_CONTROL_CODE: { color: 'purple',  help: 'A #-prefixed card the server does not recognise.' }
};
const STATUS_OPTIONS = Object.keys(STATUS_META).map((s) => ({ value: s, label: s }));

const SIGNAL_COLOR = { OK: 'green', DUP: 'blue', WAIT: 'gold', ERR: 'red' };

function fmt(iso, withDate = false) {
  if (!iso) return '—';
  const d = dayjs(iso);
  return withDate ? d.format('DD MMM HH:mm:ss') : d.format('HH:mm:ss');
}

// A device clock more than the backlog threshold behind the server means the
// record was treated as a replay rather than a live scan — which changes
// whether a floor mistake is announced or silently deferred.
function skewLabel(sec) {
  if (sec === null || sec === undefined) return '—';
  const abs = Math.abs(sec);
  if (abs < 120) return `${sec}s`;
  if (abs < 3600) return `${Math.round(sec / 60)} min`;
  return `${(sec / 3600).toFixed(1)} h`;
}

export default function ScanIngestMonitorPage() {
  const { me } = useAuth();
  const { notifySuccess, notifyError } = useAppNotifications();

  const isMaster = me?.isMaster === true;
  const canView = useMemo(() => {
    const list = Array.isArray(me?.permissions) ? me.permissions : [];
    return list.includes('*') || list.includes('erp.production.scan_ingest.view');
  }, [me]);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ items: [], total: 0, totalIsCapped: false, summary: [] });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [statuses, setStatuses] = useState([]);
  const [deviceId, setDeviceId] = useState('');
  const [barcode, setBarcode] = useState('');
  const [range, setRange] = useState([dayjs().subtract(7, 'day'), dayjs()]);
  const [selected, setSelected] = useState([]);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (statuses.length) params.status = statuses;
      if (deviceId.trim()) params.deviceId = deviceId.trim();
      if (barcode.trim()) params.barcode = barcode.trim();
      if (range && range[0] && range[1]) {
        params.from = range[0].toISOString();
        params.to = range[1].toISOString();
      }
      const resp = await scanIngestApi.list(params);
      setData(resp || { items: [], total: 0, totalIsCapped: false, summary: [] });
      // Drop selections that are no longer on screen, so a delete can never
      // act on a row the operator can no longer see.
      const visible = new Set((resp?.items || []).map((r) => r.id));
      setSelected((prev) => prev.filter((id) => visible.has(id)));
    } catch (e) {
      notifyError(e?.response?.data?.error?.message || 'Could not load scan records.');
    } finally {
      setLoading(false);
    }
  }, [page, limit, statuses, deviceId, barcode, range, notifyError]);

  useEffect(() => { if (canView) load(); }, [canView, load]);

  const doDelete = useCallback((ids) => {
    Modal.confirm({
      title: `Delete ${ids.length} scan record${ids.length === 1 ? '' : 's'}?`,
      icon: <WarningOutlined style={{ color: '#faad14' }} />,
      content: (
        <div>
          <p>
            This permanently removes {ids.length === 1 ? 'the record' : 'these records'} and
            {' '}{ids.length === 1 ? 'its' : 'their'} replay gate, so the same scan can be sent again.
          </p>
          <p style={{ marginBottom: 0 }}>
            <Text type="secondary">
              Records that already advanced a piece (APPLIED) cannot be deleted and are skipped.
            </Text>
          </p>
        </div>
      ),
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        setDeleting(true);
        try {
          const res = await scanIngestApi.remove(ids);
          const skipped = res?.skipped || [];
          if (res?.deleted) notifySuccess(`Deleted ${res.deleted} record${res.deleted === 1 ? '' : 's'}.`);
          if (skipped.length) {
            notifyError(
              `${skipped.length} kept: ${skipped.map((s) => s.status || s.reason).join(', ')}.`
            );
          }
          setSelected([]);
          await load();
        } catch (e) {
          notifyError(e?.response?.data?.error?.message || 'Delete failed.');
        } finally {
          setDeleting(false);
        }
      }
    });
  }, [load, notifySuccess, notifyError]);

  const columns = useMemo(() => [
    {
      title: 'Received', dataIndex: 'receivedAt', width: 150,
      render: (v, row) => (
        <Space direction="vertical" size={0}>
          <Text>{fmt(v, true)}</Text>
          {row.isBacklogged ? (
            <Tooltip title={`Device clock was ${skewLabel(row.clockSkewSec)} behind — treated as a buffered replay, not a live scan.`}>
              <Tag color="default" style={{ fontSize: 11 }}>
                <ClockCircleOutlined /> backlog {skewLabel(row.clockSkewSec)}
              </Tag>
            </Tooltip>
          ) : null}
        </Space>
      )
    },
    {
      title: 'Scanner', key: 'device', width: 170,
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Text strong>{row.deviceName || row.deviceId}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {row.departmentCode} · {row.scanRole === 'STAGE_IN' ? 'entry' : 'exit'}
            {row.deviceName ? ` · ${row.deviceId}` : ''}
          </Text>
        </Space>
      )
    },
    {
      title: 'Barcode', dataIndex: 'barcode', width: 160,
      render: (v, row) => v
        ? <Text code>{v}</Text>
        : <Text type="secondary">{row.recordKind}</Text>
    },
    {
      title: 'Status', dataIndex: 'status', width: 150,
      render: (v) => {
        const meta = STATUS_META[v] || { color: 'default', help: '' };
        return <Tooltip title={meta.help}><Tag color={meta.color}>{v}</Tag></Tooltip>;
      }
    },
    {
      title: 'Signal', dataIndex: 'signal', width: 80,
      render: (v) => v ? <Tag color={SIGNAL_COLOR[v] || 'default'}>{v}</Tag> : '—'
    },
    {
      title: 'Reason', key: 'reason',
      render: (_, row) => {
        if (!row.errorCode && !row.message) return <Text type="secondary">—</Text>;
        return (
          <Space direction="vertical" size={0}>
            {row.message ? <Text>{row.message}</Text> : null}
            {row.errorCode
              ? <Text type="secondary" style={{ fontSize: 12 }}>{row.errorCode}{row.attemptCount > 1 ? ` · attempt ${row.attemptCount}` : ''}</Text>
              : null}
          </Space>
        );
      }
    },
    ...(isMaster ? [{
      title: '', key: 'actions', width: 50, align: 'center',
      render: (_, row) => row.deletable
        ? (
          <Tooltip title="Delete this record">
            <Button type="text" danger size="small" icon={<DeleteOutlined />}
              onClick={() => doDelete([row.id])} />
          </Tooltip>
        )
        : (
          <Tooltip title="Applied records are permanent — deleting one would erase why the piece moved without undoing the move.">
            <DeleteOutlined style={{ color: '#d9d9d9' }} />
          </Tooltip>
        )
    }] : [])
  ], [isMaster, doDelete]);

  if (!canView) {
    return (
      <>
        <PageHeader title="Ingest Monitor" />
        <Alert type="warning" showIcon message="You do not have access to scan ingest records." />
      </>
    );
  }

  const stuck = (data.summary || []).find((s) => s.status === 'RECEIVED');
  const applied = (data.summary || []).find((s) => s.status === 'APPLIED');

  return (
    <>
      <PageHeader
        title="Ingest Monitor"
        icon={<MonitorOutlined />}
        subtitle="Every record the scanner fleet has sent, and what the engine decided about each one."
        actions={
          <Space>
            {isMaster && selected.length ? (
              <Button danger icon={<DeleteOutlined />} loading={deleting}
                onClick={() => doDelete(selected)}>
                Delete {selected.length}
              </Button>
            ) : null}
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
          </Space>
        }
      />

      {/* The "is anything stuck?" line. RECEIVED with no APPLIED is the
          signature of apply workers that never started. */}
      {stuck && stuck.count > 0 && !applied ? (
        <Alert
          type="warning" showIcon style={{ marginBottom: 16 }}
          message={`${stuck.count} record${stuck.count === 1 ? '' : 's'} accepted but never applied`}
          description="Nothing in this window reached APPLIED. The apply workers may not be running — check that SCAN_ROLLUP_WORKER_ENABLED is true on the server."
        />
      ) : null}

      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {(data.summary || []).map((s) => (
          <Col key={s.status} xs={12} sm={8} md={6} lg={4}>
            <Card size="small">
              <Statistic
                title={<Tag color={(STATUS_META[s.status] || {}).color || 'default'} style={{ marginInlineEnd: 0 }}>{s.status}</Tag>}
                value={s.count}
              />
            </Card>
          </Col>
        ))}
      </Row>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <RangePicker
            showTime allowClear={false} value={range}
            onChange={(v) => { setRange(v); setPage(1); }}
          />
          <Select
            mode="multiple" allowClear placeholder="Any status"
            style={{ minWidth: 240 }} options={STATUS_OPTIONS}
            value={statuses} onChange={(v) => { setStatuses(v); setPage(1); }}
          />
          <Input
            allowClear placeholder="Scanner id (SC00001)" style={{ width: 180 }}
            value={deviceId} onChange={(e) => setDeviceId(e.target.value)}
            onPressEnter={() => { setPage(1); load(); }}
          />
          <Input
            allowClear placeholder="Barcode" style={{ width: 180 }}
            value={barcode} onChange={(e) => setBarcode(e.target.value)}
            onPressEnter={() => { setPage(1); load(); }}
          />
          <Button icon={<SearchOutlined />} onClick={() => { setPage(1); load(); }}>Apply</Button>
        </Space>
      </Card>

      <Card size="small">
        <Table
          rowKey="id"
          size="small"
          loading={loading}
          columns={columns}
          dataSource={data.items || []}
          locale={{ emptyText: <Empty description="No scan records in this window." /> }}
          rowSelection={isMaster ? {
            selectedRowKeys: selected,
            onChange: setSelected,
            // Disabled rather than hidden, and paired with the explained
            // greyed icon in the actions column: an operator should see that
            // the row is protected, not wonder why its checkbox vanished.
            getCheckboxProps: (row) => ({ disabled: !row.deletable })
          } : undefined}
          pagination={{
            current: data.page || page,
            pageSize: data.limit || limit,
            total: data.total || 0,
            showSizeChanger: true,
            pageSizeOptions: ['25', '50', '100', '200'],
            // The server stops counting at 10,000 — an honest COUNT(*) over a
            // partitioned table sized in millions is a full scan per page turn.
            showTotal: (t) => (data.totalIsCapped ? `${t.toLocaleString('en-IN')}+ records` : `${t.toLocaleString('en-IN')} records`),
            onChange: (p, ps) => { setPage(p); setLimit(ps); }
          }}
          scroll={{ x: 1000 }}
        />
      </Card>
    </>
  );
}
